import { ASSISTANT_MODES, USER_TYPES } from './constants';

import { MESSAGE_ROLES } from './state/reducer';
import { camelCaseObjectKeys } from '/b2b/common/helpers/Format';
import { translate } from '/b2b/common/helpers/i18n';

/*
 * Maps chat-api's /v1 wire shapes onto the shapes the widget already renders, so the
 * provider and every component below it stay unaware of which backend answered. The
 * legacy webservices mappers live in ./legacyFormat.js and are untouched.
 *
 * Contract: Chat Service — API Specification
 * https://osano.atlassian.net/wiki/spaces/OE/pages/2040528900
 */

export const ESCALATION_STATUSES = {
    OPEN: 'open',
    IN_REVIEW: 'in_review',
    RESOLVED: 'resolved',
};

/**
 * An escalation the Privacy Team still owns. While one is open chat-api pauses AI
 * turns: a customer message is persisted and mirrored onto the ticket, and no reply
 * is due.
 * @param {{status?: string}|null} [escalation]
 * @returns {boolean}
 */
export const isEscalationOpen = escalation =>
    Boolean(escalation) && escalation.status !== ESCALATION_STATUSES.RESOLVED;

/**
 * Formats a chat-api conversation summary (list item or detail head).
 *
 * `mode` is deliberately `ai` for every chat-api conversation, escalated ones
 * included: it drives markdown-vs-html rendering and the sender label downstream, so
 * tagging an escalated thread `human` would render AI replies as raw markup. The
 * escalation is surfaced as its own field instead, which is what the conversation
 * list splits its tabs on.
 *
 * The two escalation fields are the two ends of one link and are not alternatives:
 * `escalation` is the escalation this conversation *is*, `escalationsRaised` the ones
 * raised *from* it, oldest-first. A conversation can carry both, and being a source is
 * not being escalated — so `escalated` is read from `escalation` alone.
 *
 * @param {object} conversation Raw conversation from chat-api
 * @returns {object} Formatted conversation
 */
export const formatConversation = conversation => {
    if (!conversation) return conversation;
    const formatted = camelCaseObjectKeys(conversation);
    const escalation = formatted.escalation || null;
    return {
        ...formatted,
        title: formatted.title || translate('aiAssistant.chat.defaultTitle'),
        mode: ASSISTANT_MODES.AI,
        escalation,
        escalated: isEscalationOpen(escalation),
        escalationsRaised: formatted.escalationsRaised || [],
    };
};

/**
 * Formats a chat-api message. Every non-customer author shares one role, so
 * `isEmployee`/`isSystem`/`author` are what tell an agent's reply and a stored system
 * row apart from an AI turn — `isSystem` is what formatConversationDetail filters on.
 * `author` comes from the `user_display_name` chat-api serializes beside `user_id`;
 * without one the view falls back to a generic label.
 *
 * @param {object} message Raw MessageResponse from chat-api
 * @returns {object} Formatted message
 */
export const formatMessage = message => {
    if (!message) return message;
    const formatted = camelCaseObjectKeys(message);
    const isEmployee = formatted.userType === USER_TYPES.EMPLOYEE;
    return {
        ...formatted,
        role:
            formatted.userType === USER_TYPES.CUSTOMER
                ? MESSAGE_ROLES.USER
                : MESSAGE_ROLES.ASSISTANT,
        isEmployee,
        isSystem: formatted.userType === USER_TYPES.SYSTEM,
        author:
            isEmployee && formatted.userDisplayName ? { name: formatted.userDisplayName } : null,
        timestamp: formatted.createdAt,
        // chat-api keeps feedback in its own resource, so a reloaded transcript
        // carries none; the thumbs state is session-local under this backend.
        feedback: formatted.metadata?.feedback || null,
        citations: formatted.citations?.length ? formatted.citations : null,
        // A reply Perun failed to generate is persisted rather than lost, marked here.
        isErrored: formatted.metadata?.status === 'errored',
    };
};

/**
 * Formats one cursor page of a conversation's messages. Callers that need the whole
 * transcript walk `nextCursor` — chat-api's cursor runs oldest-first, so following it
 * moves towards the newest message.
 *
 * `system` rows are dropped here, the one place a chat-api transcript enters the widget.
 * The only ones ever written were the two escalation lifecycle notices; chat-api no longer
 * produces them but still serves the rows already stored, and the widget now renders those
 * states from `escalation.status`, so the stored copy would say it a second time.
 *
 * @param {object} response Raw GET /v1/conversations/{id} body
 * @returns {object} Formatted conversation with a page of messages
 */
export const formatConversationDetail = (response = {}) => {
    const { messages = {}, ...conversation } = response;
    const { items = [], next_cursor: nextCursor = null } = messages || {};
    return {
        ...formatConversation(conversation),
        messages: items.map(formatMessage).filter(message => message && !message.isSystem),
        messagePagination: {
            nextCursor,
            hasNext: Boolean(nextCursor),
            pageSize: items.length || null,
        },
    };
};

/**
 * Formats chat-api's offset-paginated conversation list into the paginated envelope
 * used across the app.
 *
 * @param {object} response Raw GET /v1/conversations body
 * @returns {{results: Array, count: number, hasNextPage: boolean, limit: number, offset: number, page: number, pageSize: number}}
 */
export const formatConversationsResponse = (response = {}) => {
    const { items = [], total, limit = 50, offset = 0, has_more: hasMore = false } = response;
    return {
        results: items.map(formatConversation),
        count: total ?? items.length,
        hasNextPage: Boolean(hasMore),
        limit,
        offset,
        page: limit > 0 ? Math.floor(offset / limit) + 1 : 1,
        pageSize: limit,
    };
};

/**
 * Formats the POST /v1/escalations response. `conversationId` is the whole point of
 * it: escalation creates its own conversation and the widget navigates there.
 * `sourceConversationId` is the opposite end — the conversation it was raised from,
 * null when it was raised outside one.
 *
 * @param {object} response Raw escalation body
 * @returns {{id: string, conversationId: string, status: string, createdAt: string, resolvedAt: string|null, sourceConversationId: string|null}}
 */
export const formatEscalation = (response = {}) => {
    const {
        id,
        conversationId,
        status,
        createdAt,
        resolvedAt = null,
        sourceConversationId = null,
    } = camelCaseObjectKeys(response);
    return { id, conversationId, status, createdAt, resolvedAt, sourceConversationId };
};
